import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { toLocalTimeFormat } from '../../../utils/datetime'
import colors from '../../../constants/colors'
import Icon from '../../../components/Icon'
import { addModal } from '../../modals/modalSlice'
import { GoodiebagType } from '../../goodiebag/types'
import { toggleIsQuestionsOpened } from '../questionsSlice'
import { RootState } from '../../../store'
import { RetrievePresentationSpaceResponse } from '../../../types/swagger'
import FetchItemDetail from '../../imagemap/components/FetchItemDetail'
import useSlug from '../../../hooks/useSlug'
import {
    moderatorSelector,
    setFullScreenMode,
    toggleWidget,
    showWidgetSelector,
    toggleSidebar,
    showSidebarSelector,
    togglePublicChat,
    showPublicChatSelector,
} from '../presentationSlice'
import styles from '../../../constants/styles'
import useFloorMapLink from '../../../hooks/useFloorMapLink'
import { IconProps } from '../../../components/Icon/Icon'
import useToggleBookmark from '../../goodiebag/useToggleBookmark'
import { _ } from '../../../utils/localization'
import CustomButton from '../../../components/CustomButton'

export type Presentation = RetrievePresentationSpaceResponse['current_presentation']

const S = {
    InfoBar: styled.div<{$width: number}>`
        width: ${({ $width }) => $width}px;
        height: 66px;
        background-color: white;
        color: black;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 0 16px;
    `,

    Info: styled.div`
        overflow: hidden;
        padding-right: 10px;
    `,

    Actions: styled.div`
        display: flex;
        justify-content: space-between;
    `,

    Title: styled.h6`
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        font-weight: bold;
        color: ${colors.gray7};
    `,

    Divider: styled.span`
        margin: 0 6px;
    `,

    SubTitle: styled.span`
        display: flex;
        align-content: center;
        align-items: center;
        font-size: 12px;
    `,

    Time: styled.span`
        font-size: 12px;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: flex;
        align-items: center;
    `,

    Performer: styled.span`
        max-width: 550px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `,

    ActionIcon: styled.div<{ showBar: boolean }>`
        position: relative;
        padding: 3px 6px;
        padding-right: 8px;
        border: 3px solid transparent;
        border-right-color: ${({ showBar }) => (showBar ? '#343434' : 'transparent')};
        background: ${({ showBar }) => (showBar ? ' rgba(52, 52, 52, 0.08)' : 'transparent')};
        cursor: pointer;
        &:hover {
            filter: brightness(0.6);
        }
    `,

    IconText: styled.span`
        position: absolute;
        bottom: -3px;
        left: 50%;
        transform: translate(-50%, 50%);
        font-size: 11px;
        font-weight: 700;
        color: ${colors.icon};
        user-select: none;
    `,

    ActionSidebar: styled.div<{isShowSidebar: boolean}>`
        position: fixed;
        top: 50%;
        transform: translateY(-50%);
        right: ${({ isShowSidebar }) => (isShowSidebar ? styles.sidebarWidth : 0)}px;
        height: 400px;
        padding: 12px 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        background: white;
        border-left: 1px solid ${colors.gray3};
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        transition: 0.75s;
        @media (max-width: 1366px) {
            right: ${({ isShowSidebar }) => (isShowSidebar ? styles.smallerSidebarWidth : 0)}px;
        }
    `,
}

const ActionIcon = ({
    text, isActive, showBar, onClick, ...props
}: IconProps & { text?: string, showBar?: boolean }) => (
    <S.ActionIcon showBar={isActive && showBar} onClick={onClick}>
        <Icon
            size={24}
            {...props}
            isActive={!showBar && isActive}
        />
        <S.IconText>{text}</S.IconText>
    </S.ActionIcon>
)

const Info = ({ presentation }: { presentation: Presentation }) => (
    <S.Info>
        <S.Title>{presentation.name}</S.Title>
        <S.SubTitle>
            <S.Time>
                <span>{toLocalTimeFormat(presentation.start_date)}</span>
                <S.Divider>-</S.Divider>
                <span>{toLocalTimeFormat(presentation.end_date)}</span>
            </S.Time>
            <S.Divider>|</S.Divider>
            <S.Performer>{presentation.performer_names}</S.Performer>
        </S.SubTitle>
    </S.Info>
)

const QuestionAction = () => {
    const presentationSlug = useSlug('presentationSlug')
    const dispatch = useDispatch()
    const isOpened = useSelector(
        (state: RootState) => state.presentationRoom.questions.isOpened,
    )
    const moderator = useSelector(moderatorSelector(presentationSlug))
    const onClick = () => { dispatch(toggleIsQuestionsOpened()) }

    if (!moderator) {
        // Return span because parent has spacing between for children
        return <span />
    }

    return (
        <ActionIcon
            icon="question"
            onClick={onClick}
            isActive={isOpened}
        />
    )
}

const PublicChatAction = () => {
    const dispatch = useDispatch()
    const isActive = useSelector(showPublicChatSelector)

    const onClick = () => { dispatch(togglePublicChat()) }

    return (
        <ActionIcon
            icon="forum"
            onClick={onClick}
            isActive={isActive}
            showBar
        />
    )
}

const BookmarkAction = ({ presentation }: { presentation: Presentation }) => {
    const item = { type: GoodiebagType.ProgramItem, id: presentation.id }
    const { toggleBookmark, isBookmarked, bookmarkIcon } = useToggleBookmark(item)
    return (
        <ActionIcon
            onClick={toggleBookmark}
            icon={bookmarkIcon}
            isActive={isBookmarked}
        />
    )
}

const InfoAction = ({ presentation }: { presentation: Presentation }) => {
    const dispatch = useDispatch()
    const eventSlug = useSlug('eventSlug')
    const itemId = presentation.id
    const onClick = () => {
        const Component = () => <FetchItemDetail eventSlug={eventSlug} itemId={itemId} />
        dispatch(addModal({ Component }))
    }
    return <ActionIcon icon="info" onClick={onClick} />
}

const AttendeeListAction = () => {
    const dispatch = useDispatch()
    const isActive = useSelector(showSidebarSelector)
    return (
        <ActionIcon
            showBar
            icon="group"
            onClick={() => dispatch(toggleSidebar())}
            isActive={isActive}
        />
    )
}

const ShowWidgetAction = () => {
    const dispatch = useDispatch()
    const isActive = useSelector(showWidgetSelector)
    return (
        <ActionIcon
            showBar
            icon="gamepad"
            onClick={() => dispatch(toggleWidget())}
            isActive={isActive}
        />
    )
}

const FullScreenAction = ({ isFullScreen }: {isFullScreen: boolean}) => {
    const dispatch = useDispatch()
    return (
        <ActionIcon
            size={30}
            isActive={isFullScreen}
            icon={isFullScreen ? 'fullscreenExit' : 'fullscreen'}
            onClick={() => dispatch(setFullScreenMode(!isFullScreen))}
        />
    )
}

const ExitAction = () => {
    const history = useHistory()
    const floorMapLink = useFloorMapLink()
    const [isShow, setShow] = useState(false)
    const leavePresentationRoom = () => {
        setShow(false)
        history.push(floorMapLink)
    }
    const close = () => setShow(false)

    return (
        <>
            <ActionIcon icon="doorOpen" onClick={() => setShow(true)} text="EXIT" />
            <Modal centered show={isShow} onHide={close}>
                <Modal.Body>
                    <p>{_('presentation.confirm_leave_question')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton onClick={close} variant="danger">
                        {_('presentation.confirm_leave_button_no')}
                    </CustomButton>
                    <CustomButton onClick={leavePresentationRoom} variant="success">
                        {_('presentation.confirm_leave_button_yes')}
                    </CustomButton>
                </Modal.Footer>
            </Modal>
        </>
    )
}

interface ActionBarProps {
    presentation: Presentation
    videoWidth: number
    isFullScreen: boolean
    isShowSidebar: boolean
    chatId: number
}

const ActionSidebar = ({
    isShowSidebar, presentation, isFullScreen, chatId
}: ActionBarProps) => (
    <S.ActionSidebar isShowSidebar={isShowSidebar}>
        <ExitAction />
        <div style={{ flex: 1 }} />
        {chatId && <PublicChatAction />}
        <QuestionAction />
        <InfoAction presentation={presentation} />
        {presentation?.embedded_url && <ShowWidgetAction />}
        <BookmarkAction presentation={presentation} />
        <AttendeeListAction />
        <div style={{ flex: 1 }} />
        <FullScreenAction isFullScreen={isFullScreen} />
    </S.ActionSidebar>
)

const InfoBar = ({ presentation, videoWidth }: ActionBarProps) => (
    <S.InfoBar $width={videoWidth}>
        {presentation && <Info presentation={presentation} />}
    </S.InfoBar>
)

const ActionBar = (props: ActionBarProps) => (
    <>
        {!props.isFullScreen && <InfoBar {...props} />}
        {props.presentation && <ActionSidebar {...props} />}
    </>
)

export default ActionBar
